import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import {
  Search,
  Eye,
  Printer,
  Download,
  Trash2,
  Edit,
  RefreshCw,
  Package,
  Truck,
  Upload,
  FileSpreadsheet,
  Image as ImageIcon,
  Share2,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import Layout from "@/components/Layout";
import LabelPreview from "@/components/LabelPreview";
import ReportExportMenu from "@/components/ReportExportMenu";
import CargoLinesEditor, { computeCargoTotals } from "@/components/generator/CargoLinesEditor";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { LABEL_PRESETS, STATUS_OPTIONS, STATUS_COLOR, getPresetByValue } from "@/lib/labelSizes";
import { downloadLabelPDF, printLabelPDF } from "@/lib/pdfGenerator";
import { copyToClipboard } from "@/lib/clipboard";

const PAYMENT_STATUS_OPTIONS = ["Unpaid", "Partial", "Paid"];
const PAYMENT_STATUS_COLOR = {
  Unpaid: "bg-red-50 text-red-700 border-red-300",
  Partial: "bg-amber-50 text-amber-800 border-amber-300",
  Paid: "bg-emerald-50 text-emerald-800 border-emerald-300",
};

export default function Shipments() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [coordinatorFilter, setCoordinatorFilter] = useState("ALL");
  const [salesView, setSalesView] = useState("MARGIN"); // MARGIN | SALES | COST | ALL
  const [viewItem, setViewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [salesPersons, setSalesPersons] = useState([]);
  const [users, setUsers] = useState([]);
  const shareCardRef = useRef(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (coordinatorFilter !== "ALL") params.job_coordinator = coordinatorFilter;
      const { data } = await api.get("/codes", { params });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, coordinatorFilter]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 300);
    return () => clearTimeout(t);
  }, [fetchItems]);

  useEffect(() => {
    api.get("/meta/sales-persons")
      .then(({ data }) => setSalesPersons(data || []))
      .catch(() => setSalesPersons([]));
    api.get("/meta/users")
      .then(({ data }) => setUsers(data || []))
      .catch(() => setUsers([]));
  }, []);

  const handleStatusChange = async (item, newStatus) => {
    try {
      const { data } = await api.post(`/codes/${item.id}/status`, { status: newStatus });
      setItems((arr) => arr.map((x) => (x.id === item.id ? data : x)));
      toast.success("Status updated", { description: newStatus });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const handlePaymentStatusChange = async (item, newStatus) => {
    try {
      const { data } = await api.post(`/codes/${item.id}/payment-status`, { payment_status: newStatus });
      setItems((arr) => arr.map((x) => (x.id === item.id ? data : x)));
      toast.success("Payment status updated", { description: newStatus });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  // Inline-edit date fields (eta_date / received_at_origin / departed_date) directly from the listing.
  const handleDateFieldChange = async (item, key, value) => {
    try {
      const { data } = await api.put(`/codes/${item.id}`, { [key]: value || null });
      setItems((arr) => arr.map((x) => (x.id === item.id ? data : x)));
      const label = key === "eta_date" ? "ETA" : key === "departed_date" ? "Departed date" : "Received date";
      toast.success(`${label} updated`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const handleDelete = async (item) => {
    if (!confirm(`Delete shipment ${item.generated_code}?`)) return;
    try {
      await api.delete(`/codes/${item.id}`);
      setItems((arr) => arr.filter((x) => x.id !== item.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      customer_name: item.customer_name || "",
      company_name: item.company_name || "",
      mobile_number: item.mobile_number || "",
      email: item.email || "",
      customer_short_code: item.customer_short_code || "",
      trade_lane: item.trade_lane || "",
      shipment_mode: item.shipment_mode || "AIR",
      has_dg_battery: !!item.has_dg_battery,
      has_brand: !!item.has_brand,
      is_wooden: !!item.is_wooden,
      is_protein_supplement: !!item.is_protein_supplement,
      is_medical: !!item.is_medical,
      manual_code: item.generated_code || "",
      en_no: item.en_no || "",
      commodity: item.commodity || "",
      pickup: item.pickup || "",
      warehouse_location: item.warehouse_location || "",
      sales_person: item.sales_person || "",
      account_of_user_id: item.account_of_user_id || "",
      job_coordinator_user_id: item.job_coordinator_user_id || "",
      sales_amount: item.sales_amount ?? "",
      cost: item.cost ?? "",
      margin: item.margin ?? "",
      sales_currency: item.sales_currency || "AED",
      total_cbm: item.total_cbm ?? "",
      total_chargeable_weight: item.total_chargeable_weight ?? "",
      total_weight: item.total_weight ?? "",
      cargo_lines: Array.isArray(item.cargo_lines) ? item.cargo_lines : [],
      received_at_origin: item.received_at_origin || "",
      departed_date: item.departed_date || "",
      eta_date: item.eta_date || "",
      package_mode: item.package_mode || "auto",
      package_total: item.package_total || 1,
      package_number: item.package_number || "",
      label_number: item.label_number || "",
      label_size: item.label_size || { preset: "152x102", width_mm: 152, height_mm: 102 },
    });
  };

  const setEditField = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));

  const handleSaveEdit = async () => {
    if (!editItem || !editForm) return;
    try {
      const originalCode = editItem.generated_code || "";
      const typedCode = (editForm.manual_code || "").trim();
      // Only send manual_code override when user actually changed the code text.
      // If unchanged, leave it blank so the backend can auto-regenerate when flags/short_code change.
      const manualCodeOverride = typedCode && typedCode !== originalCode ? typedCode : "";

      const payload = {
        ...editForm,
        customer_short_code: (editForm.customer_short_code || "").toUpperCase().replace(/[^A-Z0-9]/g, ""),
        sales_amount: editForm.sales_amount === "" ? null : Number(editForm.sales_amount),
        cost: editForm.cost === "" ? null : Number(editForm.cost),
        margin: editForm.margin === "" ? null : Number(editForm.margin),
        total_cbm: editForm.total_cbm === "" ? null : Number(editForm.total_cbm),
        total_chargeable_weight: editForm.total_chargeable_weight === "" ? null : Number(editForm.total_chargeable_weight),
        total_weight: editForm.total_weight === "" ? null : Number(editForm.total_weight),
        cargo_lines: (editForm.cargo_lines || []).map((l) => ({
          package_type: l.package_type || "",
          length_cm: l.length_cm === "" || l.length_cm == null ? null : Number(l.length_cm),
          width_cm: l.width_cm === "" || l.width_cm == null ? null : Number(l.width_cm),
          height_cm: l.height_cm === "" || l.height_cm == null ? null : Number(l.height_cm),
          weight_kg: l.weight_kg === "" || l.weight_kg == null ? null : Number(l.weight_kg),
        })),
        package_total: Number(editForm.package_total) || 1,
        pickup: editForm.pickup || null,
        trade_lane: editForm.trade_lane || null,
        received_at_origin: editForm.received_at_origin || null,
        departed_date: editForm.departed_date || null,
        eta_date: editForm.eta_date || null,
        manual_code: manualCodeOverride,
      };
      const { data } = await api.put(`/codes/${editItem.id}`, payload);
      setItems((arr) => arr.map((x) => (x.id === editItem.id ? data : x)));
      toast.success("Shipment updated", { description: data.generated_code });
      setEditItem(null);
      setEditForm(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const handleEditPresetChange = (value) => {
    const p = getPresetByValue(value);
    setEditField("label_size", { preset: value, width_mm: p.width_mm, height_mm: p.height_mm });
  };

  const handleShareImage = async (mode) => {
    if (!shareCardRef.current) return;
    try {
      const canvas = await html2canvas(shareCardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
      if (mode === "download") {
        const link = document.createElement("a");
        link.download = `${viewItem.generated_code || "shipment"}.jpg`;
        link.href = dataUrl;
        link.click();
        toast.success("Image downloaded");
      } else if (mode === "whatsapp") {
        // WhatsApp can't accept image via URL — open chat with a text summary and
        // also download the JPEG so user can attach it.
        const link = document.createElement("a");
        link.download = `${viewItem.generated_code || "shipment"}.jpg`;
        link.href = dataUrl;
        link.click();
        const summary =
          `*Shipment Update*%0A` +
          `Code: ${viewItem.generated_code}%0A` +
          (viewItem.en_no ? `EN NO: ${viewItem.en_no}%0A` : "") +
          `Customer: ${viewItem.customer_name}%0A` +
          `Status: ${viewItem.status || "Pending"}%0A` +
          (viewItem.payment_status ? `Payment: ${viewItem.payment_status}%0A` : "") +
          `(Image attached separately)`;
        const url = `https://wa.me/?text=${summary}`;
        window.open(url, "_blank", "noopener");
        toast.success("WhatsApp opened — attach the downloaded JPEG");
      } else if (mode === "share" && navigator.share && navigator.canShare) {
        canvas.toBlob(async (blob) => {
          const file = new File([blob], `${viewItem.generated_code || "shipment"}.jpg`, { type: "image/jpeg" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: "Shipment Update",
              text: `${viewItem.generated_code} — ${viewItem.customer_name}`,
              files: [file],
            });
          } else {
            const link = document.createElement("a");
            link.download = `${viewItem.generated_code || "shipment"}.jpg`;
            link.href = dataUrl;
            link.click();
          }
        }, "image/jpeg", 0.92);
      }
    } catch (err) {
      console.warn("Share image failed", err);
      toast.error("Could not generate share image");
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-zinc-900 flex items-center gap-3">
              <Truck className="w-7 h-7 text-zinc-700" />
              Shipments
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              View, print and track every saved shipment label.
            </p>
          </div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
            {can("can_export") && (
              <ReportExportMenu testid="btn-export-csv" triggerLabel="Export CSV" />
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkOpen(true)}
              data-testid="btn-bulk-upload"
              className="h-9 text-xs uppercase tracking-[0.12em]"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Bulk Upload
            </Button>
            <span data-testid="shipments-count">{items.length}</span> records
          </div>
        </div>

        {/* Filters */}
        <Card className="border-zinc-200 shadow-sm mb-6">
          <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative md:col-span-2">
              <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search code, customer, EN NO, company…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-11 pl-9"
                data-testid="input-shipments-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11" data-testid="select-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={coordinatorFilter} onValueChange={setCoordinatorFilter}>
              <SelectTrigger className="h-11" data-testid="select-coordinator-filter">
                <SelectValue placeholder="All coordinators" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL" data-testid="coordinator-filter-all">All coordinators</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`coordinator-filter-${u.id}`}>
                    {u.name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {can("view_financials") && (
            <div className="px-4 pb-4 -mt-1 flex items-center gap-2 text-xs">
              <span className="text-zinc-500 uppercase tracking-[0.12em] font-semibold">
                Sales column shows:
              </span>
              <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden" data-testid="sales-view-toggle">
                {["MARGIN", "SALES", "COST", "ALL"].map((v) => (
                  <button
                    key={v}
                    onClick={() => setSalesView(v)}
                    data-testid={`sales-view-${v.toLowerCase()}`}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      salesView === v
                        ? "bg-zinc-900 text-white"
                        : "bg-white text-zinc-600 hover:bg-zinc-50"
                    }`}
                  >
                    {v === "ALL" ? "All 3" : v.charAt(0) + v.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
              <span className="text-[10px] text-zinc-400 ml-1">Finance role</span>
            </div>
          )}
        </Card>

        {/* Table-like list */}
        <Card className="border-zinc-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">EN NO</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Commodity</th>
                  <th className="px-4 py-3">Pickup</th>
                  <th className="px-4 py-3">Sales</th>
                  <th className="px-4 py-3">Packages</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {loading ? (
                  <tr><td colSpan="10" className="px-4 py-10 text-center text-zinc-500">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan="10" className="px-4 py-10 text-center text-zinc-500" data-testid="shipments-empty">No shipments yet.</td></tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id} className="hover:bg-zinc-50" data-testid={`row-${item.id}`}>
                      <td className="px-4 py-3 font-mono text-sm font-semibold text-zinc-900 whitespace-nowrap">
                        {item.generated_code}
                        <div className="text-[10px] text-zinc-500 font-sans font-normal mt-0.5">
                          {item.shipment_mode} · {(item.label_size?.width_mm) || "?"}×{(item.label_size?.height_mm) || "?"}mm
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-700">
                        {item.en_no || "—"}
                      </td>
                      <td className="px-4 py-3 text-zinc-700">
                        <div className="font-medium">{item.customer_name}</div>
                        <div className="text-[11px] text-zinc-500 truncate max-w-[180px]">{item.company_name}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-700 max-w-[160px] truncate">
                        {item.commodity || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {item.pickup === "reef_warehouse" ? (
                          <div>
                            <div className="font-medium text-zinc-800">Reef Warehouse</div>
                            <div className="text-[11px] text-zinc-500">{item.warehouse_location || "—"}</div>
                          </div>
                        ) : item.pickup === "supplier_location" ? (
                          <span className="text-zinc-800">Supplier</span>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="font-medium text-zinc-800">
                          {item.sales_person || "—"}
                        </div>
                        <SalesValueCell
                          item={item}
                          view={can("view_financials") ? salesView : "MARGIN"}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {item.package_mode === "auto"
                          ? <span><Package className="w-3 h-3 inline mr-1" />1…{item.package_total}</span>
                          : (item.package_number || "—")}
                      </td>
                      <td className="px-4 py-3">
                        <Select
                          value={item.status || "Pending"}
                          onValueChange={(v) => handleStatusChange(item, v)}
                          disabled={!can("can_change_status")}
                        >
                          <SelectTrigger
                            data-testid={`select-status-${item.id}`}
                            className={`h-8 text-xs font-medium border ${STATUS_COLOR[item.status || "Pending"] || ""}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {(item.status === "Expected ETA" || item.eta_date) && (
                          <input
                            type="date"
                            value={item.eta_date || ""}
                            disabled={!can("can_change_status")}
                            onChange={(e) => handleDateFieldChange(item, "eta_date", e.target.value)}
                            className="mt-1.5 h-7 text-[11px] font-mono px-1.5 border border-zinc-300 rounded-sm w-full focus:outline-none focus:border-blue-500"
                            data-testid={`input-eta-${item.id}`}
                            placeholder="ETA"
                          />
                        )}
                        {(item.status === "Departed from Origin Port" || item.departed_date) && (
                          <input
                            type="date"
                            value={item.departed_date || ""}
                            disabled={!can("can_change_status")}
                            onChange={(e) => handleDateFieldChange(item, "departed_date", e.target.value)}
                            className="mt-1.5 h-7 text-[11px] font-mono px-1.5 border border-zinc-300 rounded-sm w-full focus:outline-none focus:border-blue-500"
                            data-testid={`input-departed-${item.id}`}
                            placeholder="Departed"
                          />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Select
                          value={item.payment_status || "Unpaid"}
                          onValueChange={(v) => handlePaymentStatusChange(item, v)}
                          disabled={!can("can_change_payment")}
                        >
                          <SelectTrigger
                            data-testid={`select-payment-${item.id}`}
                            className={`h-8 text-xs font-medium border ${PAYMENT_STATUS_COLOR[item.payment_status || "Unpaid"] || ""}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!can("can_change_payment") && (
                          <div className="text-[10px] text-zinc-400 mt-0.5">View only</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <IconBtn title="View" onClick={() => setViewItem(item)} testid={`btn-view-${item.id}`}>
                            <Eye className="w-4 h-4" />
                          </IconBtn>
                          <IconBtn title="Print" onClick={() => printLabelPDF(item)} testid={`btn-print-${item.id}`}>
                            <Printer className="w-4 h-4" />
                          </IconBtn>
                          <IconBtn title="Download PDF" onClick={() => { downloadLabelPDF(item); toast.success("PDF downloaded"); }} testid={`btn-pdf-${item.id}`}>
                            <Download className="w-4 h-4" />
                          </IconBtn>
                          {can("can_edit") && (
                            <IconBtn title="Edit shipment" onClick={() => openEdit(item)} testid={`btn-edit-${item.id}`}>
                              <Edit className="w-4 h-4" />
                            </IconBtn>
                          )}
                          {can("can_delete") && (
                            <IconBtn title="Delete" danger onClick={() => handleDelete(item)} testid={`btn-delete-${item.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </IconBtn>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* View dialog */}
      <Dialog open={!!viewItem} onOpenChange={(o) => !o && setViewItem(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Shipment Details</DialogTitle>
            <DialogDescription>
              Customer-facing summary. You can share this view as an image via WhatsApp or download it.
            </DialogDescription>
          </DialogHeader>
          {viewItem && (
            <div ref={shareCardRef} className="bg-white p-4 rounded-md grid grid-cols-1 md:grid-cols-2 gap-5 mt-2" data-testid="view-share-card">
              <LabelPreview shipment={viewItem} pageIndex={1} />
              <div className="text-sm flex flex-col gap-2">
                <Detail label="Customer" value={viewItem.customer_name} />
                <Detail label="Company" value={viewItem.company_name} />
                <Detail label="Trade Lane" value={viewItem.trade_lane || "—"} />
                <Detail label="Mobile" value={viewItem.mobile_number} />
                <Detail label="Email" value={viewItem.email} />
                <Detail label="Short Code" value={viewItem.customer_short_code} mono />
                <Detail label="EN NO" value={viewItem.en_no || "—"} mono />
                <Detail label="Commodity" value={viewItem.commodity || "—"} />
                <Detail
                  label="Pickup"
                  value={
                    viewItem.pickup === "reef_warehouse"
                      ? `Reef Warehouse${viewItem.warehouse_location ? ` — ${viewItem.warehouse_location}` : ""}`
                      : viewItem.pickup === "supplier_location"
                        ? "Supplier Location"
                        : "—"
                  }
                />
                <Detail label="Sales Person" value={viewItem.sales_person || "—"} />
                <Detail label="Mode" value={viewItem.shipment_mode} />
                <Detail label="DG/Battery" value={viewItem.has_dg_battery ? "Yes" : "No"} />
                <Detail label="Brand" value={viewItem.has_brand ? "Yes" : "No"} />
                <Detail label="Wooden" value={viewItem.is_wooden ? "Yes" : "No"} />
                <Detail label="Protein Supp." value={viewItem.is_protein_supplement ? "Yes" : "No"} />
                <Detail label="Medical" value={viewItem.is_medical ? "Yes" : "No"} />
                <Detail
                  label="Packages"
                  value={viewItem.package_mode === "auto" ? `1…${viewItem.package_total}` : (viewItem.package_number || "—")}
                  mono
                />
                <Detail label="Status" value={viewItem.status || "Pending"} />
                <Detail label="Payment" value={viewItem.payment_status || "Unpaid"} />
                {viewItem.received_at_origin && (
                  <Detail label="Received at Origin" value={viewItem.received_at_origin} mono />
                )}
                {viewItem.departed_date && (
                  <Detail label="Departed from Origin" value={viewItem.departed_date} mono />
                )}
                {viewItem.eta_date && (
                  <Detail label="Expected ETA" value={viewItem.eta_date} mono />
                )}
                {viewItem.shipment_mode === "SEA" && viewItem.total_cbm != null && (
                  <Detail label="Total CBM" value={`${viewItem.total_cbm} m³`} mono />
                )}
                {viewItem.shipment_mode === "AIR" && viewItem.total_chargeable_weight != null && (
                  <Detail label="Chargeable Weight" value={`${viewItem.total_chargeable_weight} kg`} mono />
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => handleShareImage("download")} data-testid="view-btn-download-image">
              <ImageIcon className="w-4 h-4 mr-1.5" /> JPEG
            </Button>
            <Button variant="outline" onClick={() => handleShareImage("whatsapp")} data-testid="view-btn-share-whatsapp" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50">
              <Share2 className="w-4 h-4 mr-1.5" /> WhatsApp (JPEG)
            </Button>
            <Button variant="outline" onClick={() => printLabelPDF(viewItem)} data-testid="view-btn-print">
              <Printer className="w-4 h-4 mr-1.5" /> Print
            </Button>
            <Button onClick={() => { downloadLabelPDF(viewItem); toast.success("PDF downloaded"); }} data-testid="view-btn-pdf" className="bg-blue-700 hover:bg-blue-800 text-white">
              <Download className="w-4 h-4 mr-1.5" /> PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog — full shipment edit */}
      <Dialog open={!!editItem} onOpenChange={(o) => !o && (setEditItem(null), setEditForm(null))}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Shipment</DialogTitle>
            <DialogDescription>
              All fields editable. The shipment code is auto-regenerated when mode, pickup, DG, brand, or short-code changes.
            </DialogDescription>
          </DialogHeader>
          {editItem && editForm && (
            <div className="space-y-5 mt-2">
              {/* Customer block */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <EditField label="Customer Name">
                  <Input data-testid="edit-customer-name" value={editForm.customer_name} onChange={(e) => setEditField("customer_name", e.target.value)} />
                </EditField>
                <EditField label="Company">
                  <Input data-testid="edit-company-name" value={editForm.company_name} onChange={(e) => setEditField("company_name", e.target.value)} />
                </EditField>
                <EditField label="Mobile">
                  <Input data-testid="edit-mobile" value={editForm.mobile_number} onChange={(e) => setEditField("mobile_number", e.target.value)} />
                </EditField>
                <EditField label="Email">
                  <Input data-testid="edit-email" type="email" value={editForm.email} onChange={(e) => setEditField("email", e.target.value)} />
                </EditField>
                <EditField label="Trade Lane">
                  <Select value={editForm.trade_lane || ""} onValueChange={(v) => setEditField("trade_lane", v)}>
                    <SelectTrigger data-testid="edit-trade-lane"><SelectValue placeholder="Select route" /></SelectTrigger>
                    <SelectContent>
                      {["China to Dubai", "Dubai to Iraq", "China to EU", "China to KSA"].map((tl) => (
                        <SelectItem key={tl} value={tl}>{tl}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </EditField>
                <EditField label="Received at Origin Warehouse">
                  <Input data-testid="edit-received-origin" type="date" value={editForm.received_at_origin || ""} onChange={(e) => setEditField("received_at_origin", e.target.value)} />
                </EditField>
                <EditField label="Departed from Origin Port">
                  <Input data-testid="edit-departed-date" type="date" value={editForm.departed_date || ""} onChange={(e) => setEditField("departed_date", e.target.value)} />
                </EditField>
                <EditField label="Expected ETA">
                  <Input data-testid="edit-eta-date" type="date" value={editForm.eta_date || ""} onChange={(e) => setEditField("eta_date", e.target.value)} />
                </EditField>
                <EditField label="Short Code">
                  <Input data-testid="edit-short-code" value={editForm.customer_short_code} onChange={(e) => setEditField("customer_short_code", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} className="font-mono" />
                </EditField>
                <EditField label="EN NO">
                  <Input data-testid="edit-en-no" value={editForm.en_no} onChange={(e) => setEditField("en_no", e.target.value)} className="font-mono" />
                </EditField>
                <EditField label="Commodity">
                  <Input data-testid="edit-commodity" value={editForm.commodity} onChange={(e) => setEditField("commodity", e.target.value)} />
                </EditField>
                <EditField label="Mode">
                  <Select value={editForm.shipment_mode} onValueChange={(v) => setEditField("shipment_mode", v)}>
                    <SelectTrigger data-testid="edit-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AIR">AIR (DA)</SelectItem>
                      <SelectItem value="SEA">SEA (DS)</SelectItem>
                    </SelectContent>
                  </Select>
                </EditField>
                <EditField label="DG/Battery">
                  <Select value={editForm.has_dg_battery ? "yes" : "no"} onValueChange={(v) => setEditField("has_dg_battery", v === "yes")}>
                    <SelectTrigger data-testid="edit-dg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes (adds T)</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </EditField>
                <EditField label="Brand">
                  <Select value={editForm.has_brand ? "yes" : "no"} onValueChange={(v) => setEditField("has_brand", v === "yes")}>
                    <SelectTrigger data-testid="edit-brand"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes (adds B)</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </EditField>
                <EditField label="Wooden">
                  <Select value={editForm.is_wooden ? "yes" : "no"} onValueChange={(v) => setEditField("is_wooden", v === "yes")}>
                    <SelectTrigger data-testid="edit-wooden"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes (adds W)</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </EditField>
                <EditField label="Protein Supplement">
                  <Select value={editForm.is_protein_supplement ? "yes" : "no"} onValueChange={(v) => setEditField("is_protein_supplement", v === "yes")}>
                    <SelectTrigger data-testid="edit-protein"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes (adds BB)</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </EditField>
                <EditField label="Medical">
                  <Select value={editForm.is_medical ? "yes" : "no"} onValueChange={(v) => setEditField("is_medical", v === "yes")}>
                    <SelectTrigger data-testid="edit-medical"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes (adds M)</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </EditField>
                <EditField label="Shipment Code (edit to override)">
                  <Input data-testid="edit-manual-code" value={editForm.manual_code || ""} onChange={(e) => setEditField("manual_code", e.target.value)} placeholder="Same-as-current, or type a new code" className="font-mono uppercase" />
                </EditField>
                <EditField label="Pickup">
                  <Select value={editForm.pickup || "none"} onValueChange={(v) => setEditField("pickup", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="edit-pickup"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="reef_warehouse">Reef Warehouse</SelectItem>
                      <SelectItem value="supplier_location">Supplier Location</SelectItem>
                    </SelectContent>
                  </Select>
                </EditField>
                <EditField label="Warehouse Location">
                  <Input data-testid="edit-warehouse" value={editForm.warehouse_location} onChange={(e) => setEditField("warehouse_location", e.target.value)} placeholder="e.g. Yiwu" />
                </EditField>
                <EditField label="Sales Person">
                  <Select value={editForm.sales_person || ""} onValueChange={(v) => setEditField("sales_person", v)}>
                    <SelectTrigger data-testid="edit-sales-person"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.name || u.email}>{u.name || u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </EditField>
                <EditField label="Job Coordinator">
                  <Select value={editForm.job_coordinator_user_id || ""} onValueChange={(v) => setEditField("job_coordinator_user_id", v)}>
                    <SelectTrigger data-testid="edit-job-coordinator"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </EditField>
                <EditField label="Currency">
                  <Select value={editForm.sales_currency} onValueChange={(v) => setEditField("sales_currency", v)}>
                    <SelectTrigger data-testid="edit-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["USD","CNY","INR","AED","EUR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </EditField>
                <EditField label="Sales Amount">
                  <Input data-testid="edit-sales-amount" type="number" step="0.01" value={editForm.sales_amount} onChange={(e) => setEditField("sales_amount", e.target.value)} className="font-mono" />
                </EditField>
                <EditField label="Cost">
                  <Input data-testid="edit-cost" type="number" step="0.01" value={editForm.cost} onChange={(e) => setEditField("cost", e.target.value)} className="font-mono" />
                </EditField>
                <EditField label="Margin (auto if blank)">
                  <Input data-testid="edit-margin" type="number" step="0.01" value={editForm.margin} onChange={(e) => setEditField("margin", e.target.value)} className="font-mono" />
                </EditField>
                {editForm.shipment_mode === "SEA" && (
                  <EditField label="Total CBM">
                    <Input data-testid="edit-cbm" type="number" step="0.001" value={editForm.total_cbm} onChange={(e) => setEditField("total_cbm", e.target.value)} className="font-mono" />
                  </EditField>
                )}
                {editForm.shipment_mode === "AIR" && (
                  <EditField label="Chargeable Weight (kg)">
                    <Input data-testid="edit-cw" type="number" step="0.01" value={editForm.total_chargeable_weight} onChange={(e) => setEditField("total_chargeable_weight", e.target.value)} className="font-mono" />
                  </EditField>
                )}
                <EditField label="Package Mode">
                  <Select value={editForm.package_mode} onValueChange={(v) => setEditField("package_mode", v)}>
                    <SelectTrigger data-testid="edit-pkg-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Automatic</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </EditField>
                {editForm.package_mode === "auto" ? (
                  <EditField label="Package Total">
                    <Input data-testid="edit-pkg-total" type="number" min="1" value={editForm.package_total} onChange={(e) => setEditField("package_total", e.target.value)} className="font-mono" />
                  </EditField>
                ) : (
                  <EditField label="Label Number">
                    <Input data-testid="edit-label-number" value={editForm.label_number} onChange={(e) => setEditField("label_number", e.target.value)} className="font-mono" />
                  </EditField>
                )}
              </div>

              {/* Cargo lines editor */}
              <div className="border-t border-zinc-200 pt-4">
                <CargoLinesEditor
                  value={editForm.cargo_lines || []}
                  onChange={(next) => {
                    const isAir = editForm.shipment_mode === "AIR";
                    const t = computeCargoTotals(next, isAir);
                    setEditForm((f) => ({
                      ...f,
                      cargo_lines: next,
                      total_weight: next.length ? String(t.total_weight) : f.total_weight,
                      total_cbm: next.length ? String(t.total_cbm) : f.total_cbm,
                      total_chargeable_weight: next.length && isAir ? String(t.total_chargeable_weight) : f.total_chargeable_weight,
                    }));
                  }}
                  isAir={editForm.shipment_mode === "AIR"}
                />
              </div>

              {/* Label size */}
              <div className="border-t border-zinc-200 pt-4">
                <div className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">Label Size</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <EditField label="Preset">
                    <Select value={editForm.label_size.preset} onValueChange={handleEditPresetChange}>
                      <SelectTrigger data-testid="edit-size-preset"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LABEL_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </EditField>
                  <EditField label="W (mm)">
                    <Input data-testid="edit-size-width" type="number" value={editForm.label_size.width_mm} onChange={(e) => setEditField("label_size", { ...editForm.label_size, preset: "custom", width_mm: Number(e.target.value) || 0 })} className="font-mono" />
                  </EditField>
                  <EditField label="H (mm)">
                    <Input data-testid="edit-size-height" type="number" value={editForm.label_size.height_mm} onChange={(e) => setEditField("label_size", { ...editForm.label_size, preset: "custom", height_mm: Number(e.target.value) || 0 })} className="font-mono" />
                  </EditField>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditItem(null); setEditForm(null); }}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="edit-save">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bulk upload dialog */}
      <BulkUploadDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onCompleted={fetchItems} />
    </Layout>
  );
}

function BulkUploadDialog({ open, onClose, onCompleted }) {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/codes/bulk-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "shipments_bulk_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Template download failed");
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/codes/bulk", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Created ${data.created_count} shipment(s)`, {
        description: data.errors.length ? `${data.errors.length} row(s) had errors` : "All rows succeeded",
      });
      onCompleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Bulk Upload Shipments (CSV)</DialogTitle>
          <DialogDescription>
            Upload a CSV with columns: <span className="font-mono">short_code, en_no, mode, dg, brand, package_total</span>.
            One shipment per row. Codes are auto-generated server-side.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <button
            type="button"
            onClick={downloadTemplate}
            className="flex items-center gap-2 text-xs text-blue-700 hover:underline"
            data-testid="bulk-download-template"
          >
            <FileSpreadsheet className="w-4 h-4" /> Download CSV template
          </button>

          <form onSubmit={handleUpload} className="space-y-3">
            <Input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="h-11"
              data-testid="bulk-file-input"
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Close
              </Button>
              <Button
                type="submit"
                disabled={submitting || !file}
                className="bg-blue-700 hover:bg-blue-800 text-white"
                data-testid="bulk-submit"
              >
                <Upload className="w-4 h-4 mr-1.5" />
                {submitting ? "Uploading…" : "Upload & Generate"}
              </Button>
            </DialogFooter>
          </form>

          {result && (
            <div className="mt-2 border border-zinc-200 rounded-md p-3 text-xs space-y-2 max-h-64 overflow-y-auto">
              <div className="font-semibold text-zinc-900">
                {result.created_count} shipment(s) created
                {result.errors.length > 0 && (
                  <span className="text-red-600 ml-2">
                    · {result.errors.length} error(s)
                  </span>
                )}
              </div>
              {result.errors.length > 0 && (
                <ul className="space-y-1 font-mono text-red-700">
                  {result.errors.map((err) => (
                    <li key={`${err.row}-${err.error}`} data-testid={`bulk-err-${err.row}`}>
                      Row {err.row}: {err.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SalesValueCell({ item, view }) {
  const ccy = item.sales_currency || "AED";
  const fmt = (n) => `${Number(n).toLocaleString()} ${ccy}`;
  const has = (v) => v != null && v !== "";

  if (view === "SALES") {
    return has(item.sales_amount) ? (
      <div className="text-[11px] text-zinc-500 font-mono" data-testid="sales-cell-sales">
        Sales: {fmt(item.sales_amount)}
      </div>
    ) : null;
  }
  if (view === "COST") {
    return has(item.cost) ? (
      <div className="text-[11px] text-zinc-500 font-mono" data-testid="sales-cell-cost">
        Cost: {fmt(item.cost)}
      </div>
    ) : null;
  }
  if (view === "ALL") {
    return (
      <div className="text-[11px] text-zinc-500 font-mono leading-tight" data-testid="sales-cell-all">
        {has(item.sales_amount) && <div>Sales: {fmt(item.sales_amount)}</div>}
        {has(item.cost) && <div>Cost: {fmt(item.cost)}</div>}
        {has(item.margin) && (
          <div className={Number(item.margin) < 0 ? "text-red-600" : ""}>
            Margin: {fmt(item.margin)}
          </div>
        )}
      </div>
    );
  }
  // Default: MARGIN
  return has(item.margin) ? (
    <div
      className={`text-[11px] font-mono ${Number(item.margin) < 0 ? "text-red-600" : "text-zinc-500"}`}
      data-testid="sales-cell-margin"
    >
      Margin: {fmt(item.margin)}
    </div>
  ) : null;
}



function IconBtn({ children, onClick, title, testid, danger = false }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-2 rounded-md border transition-colors ${
        danger
          ? "border-zinc-200 text-zinc-500 hover:text-red-600 hover:border-red-300 hover:bg-red-50"
          : "border-zinc-200 text-zinc-600 hover:text-blue-700 hover:border-blue-300 hover:bg-blue-50"
      }`}
    >
      {children}
    </button>
  );
}

function Detail({ label, value, mono = false }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500">{label}</div>
      <div className={`col-span-2 text-sm text-zinc-800 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function EditField({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
